import useFetchDataForMU, { MagicUploadFileData } from '../../useFetchDataForMU';
import styles from './MagicUploadFilesTable.module.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CircularSearchBar from '../CircularSearchBar/CircularSearchBar';
import CircularSelect from '../CircularSelect/CircularSelect';
import { deleteDoc, getDocStatus, pollZipDocStatuses } from '../../MagicUploadHelper';
import { useSearchParams } from 'react-router-dom';
import AIText from '../../../AIText/AIText';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../../../app/hooks';
import DocxDrawer from '../../../DocumentViewer/DocxDrawer';
import FileViewerDrawer from '../../../DocumentViewer/FileViewerDrawer';
import { SheetEditorDrawer } from '../../../SheetEditor';
import useMagicUploadFileOpen from './useMagicUploadFileOpen';
import { random } from 'lodash';
import dayjs from 'dayjs';
import { Mixpanel } from '../../../../utils/mixpanel';
import { INVESTOR_AICA_ADD_LEAD_FROM_DOCUMENT } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { Player } from '@lottiefiles/react-lottie-player';
import { PASSWORD_PROTECTION_STATUS } from '../../Utils/PasswordProtectionUtils';
import { getSubdomain, isEmpty } from '../../../../utils/utils';
import FilteredHeader from '../FilteredHeader/FilteredHeader';
import FilteredPills from '../FilteredPills/FilteredPills';
import OpenMuOrgIcon from '../../../../assets/Svg/OpenMUOrgIcon';
import EmptyDataState from './Components/EmptyDataState';
import MagicUploadRows from './Components/MagicUploadRows';
import OrgNameCell from './Components/OrgNameCell';
import DocTypeCell from './Components/DocTypeCell';
import AddToWatchlistV2 from '../../../../modules/Investor/Portfolio/components/AddToWatchlist/AddToWatchlistV2';
import { AddToWatchlistPrefill } from '../../../../modules/Investor/Portfolio/components/AddToWatchlist/prefillBuilder';
import BulkAddModal from '../../../BulkAdd/BulkAddModal';
import { buildBulkAddPrefillText } from '../../../BulkAdd/buildBulkAddPrefillText';
import { useVisibilityConfig, useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';
import { createMagicSignRecord } from '../../../MagicSign/MagicSignService';
import { MAGIC_SIGN_ALLOWED_EXTENSIONS, MagicSignSource } from '../../../MagicSign/constants';
import { isSignableFileName } from '../../../MagicSign/utils';

const MAX_SIMULTANEOUS_ANIMATIONS = 5; // Limit the number of animations

const MagicUploadFilesTable = ({
    tabState,
    fileData,
    setFileData,
    onListLoadingChange,
}: {
    tabState: string;
    fileData: MagicUploadFileData[];
    setFileData: React.Dispatch<React.SetStateAction<MagicUploadFileData[]>>;
    /** Fired true immediately before, and false after (success or error), the page-0 list fetch. */
    onListLoadingChange?: (loading: boolean) => void;
}) => {
    const [queryParams, setQueryParams] = useSearchParams();
    const {
        orgList,
        setSearchText,
        setIndex,
        index,
        selectOrg,
        documentList,
        selectDocType,
        fetchFileData,
        downloadTheFile,
        getDocumentList,
        getUntaggedStatus,
        setUpdatedFileData,
        submitPassword,
        isSelectingOrg,
        isMasterId,
        isSelectingDocType,
    } = useFetchDataForMU(tabState, fileData, setFileData, true);

    // Fallback to storage if Redux isn't hydrated yet (prevents 401 on page refresh)
    const investorOrgIdFromRedux = useAppSelector((state) => state.user.userData.investorOrgId);
    const investorOrgId = investorOrgIdFromRedux || sessionStorage.getItem('org_id') || localStorage.getItem('org_id') || '';
    const { magicSignVisibility } = useFeatureVisibility({ magicSignVisibility: VISIBILITY_KEYS.MAGIC_SIGN.ENABLED });

    const [loading, setLoading] = useState({});
    const [loaderWidth, setLoaderWidth] = useState({});
    const loggedInUser = useAppSelector((state) => state.user.userData);
    const [showOptions, setShowOptions] = useState<number | null>(null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const zipPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const {
        activeFile,
        openDrawer,
        fileUrl: openFileUrl,
        fileMimeType: openFileMimeType,
        openFile,
        closeFile,
        isAddable,
        handleAddToReports,
        addingToReports: addingFileToReports,
        addedToReports: addedFileToReports,
        unsavedEditsPromptTracker,
    } = useMagicUploadFileOpen(downloadTheFile);
    const [processingList, setProcessingList] = useState<string[]>([]);
    const [zipProcessingList, setZipProcessingList] = useState<string[]>([]);
    const isRefreshingRef = useRef<boolean>(false);
    const dropdownRef = useRef<HTMLTableDataCellElement>(null);
    const isCompany = queryParams.get('isCompany') === 'true';
    const magicUpload = queryParams.get('magicUpload');
    const [animationQueue, setAnimationQueue] = useState<string[]>([]);
    const [filters, setFilters] = useState<{
        assesseeOrgIdList: { value: string; label: string }[];
        userIds: { value: string; label: string }[];
        sources: { value: string; label: string }[];
    }>({
        assesseeOrgIdList: [],
        userIds: [],
        sources: [],
    });
    const [showAddLeadModal, setShowAddLeadModal] = useState(false);
    const [addLeadPrefill, setAddLeadPrefill] = useState<AddToWatchlistPrefill | undefined>(undefined);
    const currentFileForModal = useRef<MagicUploadFileData | null>(null);
    const { canWrite } = useVisibilityConfig();
    const rerouteAddLead = canWrite(VISIBILITY_KEYS.LEAD.BULK_ADD_REROUTE_ENABLED);

    // Pagination states
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const fetchMoreData = useCallback(() => {
        if (isLoadingMore || !hasMore) return;
        setIsLoadingMore(true);
        fetchFileData(page + 1, filters)
            .then(({ isFetched, hasMore }) => {
                setPage((prevPage) => prevPage + 1);
                setHasMore(hasMore);
                setIsLoadingMore(false);
            })
            .catch(() => {
                setHasMore(false);
                setIsLoadingMore(false);
            });
    }, [isLoadingMore, hasMore, page, filters, fetchFileData]);

    const ignoreNextFiltersEffect = useRef(false);

    // Overlapping page-0 fetches (e.g. the mount effect's own setFilters cascading into the
    // filters-change effect) must keep the drain gate open until ALL of them have settled — a
    // single boolean would let the first fetch's `finally` report `false` while a second is
    // still in flight, letting DrawerContent drain against stale data. Track an in-flight count
    // instead: report `true` on the first concurrent fetch, `false` only once the count reaches 0.
    const inFlightListFetchCountRef = useRef(0);

    const beginListFetch = useCallback(() => {
        inFlightListFetchCountRef.current += 1;
        if (inFlightListFetchCountRef.current === 1) {
            onListLoadingChange?.(true);
        }
    }, [onListLoadingChange]);

    const endListFetch = useCallback(() => {
        inFlightListFetchCountRef.current = Math.max(0, inFlightListFetchCountRef.current - 1);
        if (inFlightListFetchCountRef.current === 0) {
            onListLoadingChange?.(false);
        }
    }, [onListLoadingChange]);

    useEffect(() => {
        if (magicUpload !== 'true') return;

        const userId = sessionStorage.getItem('platform_user_id') || localStorage.getItem('platform_user_id');

        // Reset page and hasMore
        setPage(0);
        setHasMore(true);

        // Determine new filters based on tabState
        let newFilters: any =
            !isCompany && queryParams.get('filters') !== 'removed'
                ? {
                      assesseeOrgIdList: [],
                      userIds: [{ value: userId || '', label: loggedInUser?.userName || '' }],
                      sources: [],
                  }
                : { assesseeOrgIdList: [], userIds: [], sources: [] };

        // Set flag to ignore the next filters effect
        if (queryParams.get('showUntaggedOrg')) {
            newFilters = {
                assesseeOrgIdList: [
                    {
                        value: 'Untagged',
                        label: 'Untagged',
                    },
                ],
                userIds: [{ value: userId || '', label: loggedInUser?.userName || '' }],
                sources: [],
            };
        }
        ignoreNextFiltersEffect.current = true;
        setFilters(newFilters);

        // Fetch data immediately with new filters
        beginListFetch();
        fetchFileData(0, newFilters).finally(endListFetch);
    }, [tabState, magicUpload]);

    useEffect(() => {
        if (ignoreNextFiltersEffect.current) {
            ignoreNextFiltersEffect.current = false;
            return;
        }
        if (magicUpload !== 'true') return;

        beginListFetch();
        fetchFileData(0, filters).finally(endListFetch);
        setPage(0);
        setHasMore(true);
    }, [filters, magicUpload]);

    const handleScroll = useCallback(() => {
        if (scrollContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 50) {
                fetchMoreData();
            }
        }
    }, [fetchMoreData]);

    useEffect(() => {
        const scrollDiv = scrollContainerRef.current;
        if (scrollDiv) {
            scrollDiv.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (scrollDiv) {
                scrollDiv.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll]);

    useEffect(() => {
        if (fileData?.length > 0) {
            fileData?.forEach((file) => {
                if (file?.status !== 'tagged' && file?.status !== 'pre-tagged') {
                    if (loading[file?.id]) return;
                    setLoading((prev) => ({ ...prev, [file?.id]: true }));
                    setLoaderWidth((prev) => ({ ...prev, [file?.id]: 0.5 }));
                }
            });
        }
    }, [fileData]);

    const handleOpenAddLead = useCallback((prefill: string, file: MagicUploadFileData) => {
        currentFileForModal.current = file;
        setAddLeadPrefill({
            identifierDisplay: prefill,
            identifierPayload: prefill,
            searchText: prefill,
            shouldOpenDropdown: true,
            allowIdentifierOverwrite: true,
            companyInfoLoading: true,
        });
        setShowAddLeadModal(true);
    }, []);

    const closeAddLeadModal = useCallback(() => {
        setShowAddLeadModal(false);
        setAddLeadPrefill(undefined);
        currentFileForModal.current = null;
    }, []);

    const handleAddLeadSuccess = useCallback(
        async (leadData) => {
            if (leadData && currentFileForModal.current) {
                const result = await selectOrg(
                    {
                        orgName: leadData.orgName,
                        assesseeOrgId: leadData.assesseeOrgId,
                    },
                    currentFileForModal.current,
                );
                if (result) {
                    setIndex(null);
                    setSearchText('');
                    Mixpanel.track(INVESTOR_AICA_ADD_LEAD_FROM_DOCUMENT, {
                        assesseeOrgId: leadData.assesseeOrgId,
                        orgName: leadData.orgName,
                        source: 'magic_upload',
                    });
                }
            }
            setShowAddLeadModal(false);
            setAddLeadPrefill(undefined);
            currentFileForModal.current = null;
        },
        [selectOrg, setIndex, setSearchText, setShowAddLeadModal, setAddLeadPrefill],
    );

    useEffect(() => {
        const timeouts: NodeJS.Timeout[] = [];
        fileData?.forEach((file) => {
            if (file?.status === 'pre-tagged') {
                const timeout = setTimeout(() => {
                    setUpdatedFileData({
                        [file?.id]: {
                            ...file,
                            status: 'tagged',
                        },
                    });
                }, 2500);
                timeouts.push(timeout);
                // Update the animation queue
                setAnimationQueue((prev) => prev.filter((id) => id !== file.id));
            }
        });
        return () => {
            timeouts.forEach(clearTimeout);
        };
    }, [processingList, fileData]);

    // Update animation queue when fileData changes
    useEffect(() => {
        let isSubscribed = true;
        const preTaggedFiles = fileData?.filter((file) => file.status === 'pre-tagged')?.slice(0, MAX_SIMULTANEOUS_ANIMATIONS);

        if (preTaggedFiles?.length > 0 && isSubscribed) {
            setAnimationQueue(preTaggedFiles.map((file) => file.id));
        } else {
            if (isSubscribed) {
                setAnimationQueue([]);
            }
        }
        return () => {
            isSubscribed = false;
        };
    }, [fileData]);
    const renderThreeDotsOptions = useCallback(
        (file) => {
            const canSendForSigning = magicSignVisibility.show && isSignableFileName(file?.fileName ?? '', MAGIC_SIGN_ALLOWED_EXTENSIONS);
            return (
                <>
                    <span
                        onMouseDown={async (e) => {
                            e.preventDefault();
                            await downloadTheFile(file?.docId, file?.fileName);
                        }}
                    >
                        Download
                    </span>
                    <span
                        onMouseDown={(e) => {
                            e.preventDefault();
                            openFile(file);
                        }}
                    >
                        View
                    </span>
                    {isAddable(file) && (
                        <span
                            onMouseDown={async (e) => {
                                e.preventDefault();
                                await handleAddToReports(file);
                            }}
                        >
                            Add to Workspace
                        </span>
                    )}
                    {canSendForSigning && (
                        <span
                            onMouseDown={async (e) => {
                                e.preventDefault();
                                try {
                                    // DocStore row already exists (MU registration happened at upload time).
                                    // Call /magic/sign/create directly with the Mnemosyne doc id.
                                    // file.docId IS the mnemosyneDocId — it is the id returned by
                                    // saveDocMetaDataInDocService and stored as docId on MU rows.
                                    await createMagicSignRecord({
                                        mnemosyneDocId: String(file?.docId ?? ''),
                                        fileName: file?.fileName,
                                        source: MagicSignSource.MAGIC_UPLOAD,
                                        // Untagged/orphan rows carry orgId:'' — normalise to undefined so an
                                        // empty string is never sent as a "known" org.
                                        orgId: file?.orgId || undefined,
                                    });
                                    toast.success('Added to Magic Sign.');
                                } catch (error) {
                                    console.error('[MagicUploadFilesTable] Failed to add document to Magic Sign', error);
                                    toast.error(
                                        error instanceof Error && error.message ? error.message : 'Failed to add document to Magic Sign.',
                                    );
                                }
                            }}
                        >
                            Add to Magic Sign
                        </span>
                    )}
                    <span
                        onMouseDown={async (e) => {
                            e.preventDefault();
                            await deleteDocHandler(file?.id, false);
                        }}
                        style={{ color: '#DF666A' }}
                    >
                        Delete
                    </span>
                </>
            );
        },
        [downloadTheFile, openFile, isAddable, handleAddToReports, magicSignVisibility.show],
    );

    useEffect(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current as NodeJS.Timeout);
        }
        setProcessingList([]);
    }, [tabState]);

    useEffect(() => {
        if (investorOrgId) getDocumentList();
    }, [investorOrgId]);

    const handleClickOutside = useCallback(
        (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowOptions(null);
            }
        },
        [dropdownRef],
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    const pollZipStatus = useCallback(
        async (docIds) => {
            await pollZipDocStatuses(
                { ids: docIds },
                {
                    onSuccess: (res) => {
                        docIds.forEach(async (docId) => {
                            if (res?.[docId]?.complete) {
                                // Check if there were duplicate files
                                if (res[docId]?.duplicateFileCount > 0) {
                                    // Use immediate refresh that prevents duplicate calls
                                    refreshForDuplicates(res[docId].duplicateFileCount);
                                } else {
                                    // All files are new - use existing processing logic
                                    res[docId]?.ids?.forEach((id) => {
                                        setProcessingList((prev: any) => {
                                            if (!prev.includes(id)) {
                                                return [...prev, id];
                                            }
                                            return prev;
                                        });
                                    });
                                }
                                setUpdatedFileData({
                                    [docId]: undefined,
                                });
                                await deleteDocHandler(docId, true);
                                setZipProcessingList((prev) => prev.filter((id) => id !== docId));
                            }
                        });
                    },
                },
            );
        },
        [setUpdatedFileData, setProcessingList, setZipProcessingList, fetchFileData],
    );

    // Immediate refresh function that prevents duplicate calls
    const refreshForDuplicates = useCallback(
        async (duplicateCount: number) => {
            // Prevent multiple simultaneous refreshes
            if (isRefreshingRef.current) {
                return;
            }

            isRefreshingRef.current = true;
            beginListFetch();

            try {
                await fetchFileData(0, filters);
            } finally {
                isRefreshingRef.current = false;
                endListFetch();
            }
        },
        [fetchFileData, filters, beginListFetch, endListFetch],
    );

    const pollStatus = useCallback(
        async (docIds) => {
            await getDocStatus(
                { ids: docIds },
                {
                    onSuccess: (res) => {
                        if (res.length > 0) {
                            res.forEach((doc) => {
                                if (doc.status === 'tagged') {
                                    setProcessingList((prev) => prev.filter((id) => id !== doc.id));
                                    // Update the file data
                                    setUpdatedFileData({
                                        [doc.id]: {
                                            ...doc,
                                            status: 'pre-tagged',
                                            lastUpdated: dayjs(doc.lastUpdated).format('hh:mm A, DD MMM YYYY'),
                                        },
                                    });
                                    // Update the animation queue
                                    setAnimationQueue((prev) => prev.filter((id) => id !== doc.id));
                                    if (doc.orgName) {
                                        Mixpanel.track('Company Name Tagged', {
                                            fileName: doc.fileName,
                                            'Company Name': doc.orgName,
                                            source: 'Website',
                                            uploadedBy: doc.uploadedBy,
                                            'Tagged By': 'System',
                                            AicaDomain: getSubdomain(),
                                        });
                                    }
                                    if (doc.docType) {
                                        Mixpanel.track('Document Type Tagged', {
                                            fileName: doc.fileName,
                                            'Document Type': doc.docType,
                                            source: 'Website',
                                            uploadedBy: doc.uploadedBy,
                                            'Tagged By': 'System',
                                            AicaDomain: getSubdomain(),
                                        });
                                    }
                                    getUntaggedStatus();
                                    // Remove the file from the loading list
                                    setLoading((prev) => {
                                        const newLoading = { ...prev };
                                        delete newLoading[doc.id];
                                        return newLoading;
                                    });
                                } else {
                                    setUpdatedFileData({
                                        [doc.id]: {
                                            ...doc,
                                            status: doc.status === 'tagging' ? 'tagging' : 'processing',
                                            lastUpdated: dayjs(doc.lastUpdated).format('DD MMM YYYY'),
                                        },
                                    });
                                }
                            });
                        } else {
                            clearInterval(pollingIntervalRef.current as NodeJS.Timeout);
                        }
                        // Increase the loader width of all the files
                        setLoaderWidth((prev) => {
                            const newLoaderWidth = { ...prev };
                            docIds.forEach((id) => {
                                if (newLoaderWidth[id] < 75) {
                                    newLoaderWidth[id] += random(3.5, 10);
                                } else if (newLoaderWidth[id] > 75 && newLoaderWidth[id] < 100) {
                                    newLoaderWidth[id] += random(0.1, 1.9);
                                }
                            });
                            return newLoaderWidth;
                        });
                    },
                    onError: () => {
                        clearInterval(pollingIntervalRef.current as NodeJS.Timeout);
                    },
                },
            );
        },
        [fileData, getUntaggedStatus, setUpdatedFileData, setProcessingList, setLoading, setLoaderWidth, pollingIntervalRef],
    );

    const deleteDocHandler = useCallback(
        async (docId, hideToast) => {
            await deleteDoc(
                { id: docId },
                {
                    onSuccess: () => {
                        if (hideToast) {
                            setZipProcessingList((prev) => prev.filter((id) => id !== docId));
                            return;
                        }
                        setUpdatedFileData({ [docId]: undefined });
                        !hideToast && toast.success('Document Deleted Successfully');
                    },
                },
            );
        },
        [setUpdatedFileData],
    );

    useEffect(() => {
        if (fileData?.length > 0) {
            fileData?.forEach((file) => {
                if (
                    file.status !== 'tagged' &&
                    file.status !== 'pre-tagged' &&
                    file.id &&
                    !file.id.includes('temp') &&
                    ![PASSWORD_PROTECTION_STATUS.PASSWORD_PROTECTED, PASSWORD_PROTECTION_STATUS.PASSWORD_INVALID].includes(
                        file.passwordProtectionStatus ?? '',
                    ) &&
                    !file.zip &&
                    !processingList.includes(file.id)
                ) {
                    setProcessingList((prev: string[]) => [...prev, file.id]);
                } else if (
                    file.zip &&
                    file.id &&
                    !file.id.includes('temp') &&
                    ![PASSWORD_PROTECTION_STATUS.PASSWORD_PROTECTED, PASSWORD_PROTECTION_STATUS.PASSWORD_INVALID].includes(
                        file.passwordProtectionStatus ?? '',
                    )
                ) {
                    setZipProcessingList((prev: string[]) => (prev.includes(file.id) ? prev : [...prev, file.id]));
                }
            });
        }
    }, [fileData, processingList]);

    useEffect(() => {
        if (zipProcessingList.length > 0) {
            if (zipPollingIntervalRef.current) {
                clearInterval(zipPollingIntervalRef.current);
            }
            zipPollingIntervalRef.current = setInterval(() => {
                pollZipStatus(zipProcessingList);
            }, 1000);
        } else {
            if (zipPollingIntervalRef.current) {
                clearInterval(zipPollingIntervalRef.current);
            }
        }
        return () => {
            if (zipPollingIntervalRef.current) {
                clearInterval(zipPollingIntervalRef.current);
            }
        };
    }, [zipProcessingList, pollZipStatus]);

    useEffect(() => {
        if (processingList.length > 0) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            pollingIntervalRef.current = setInterval(() => {
                pollStatus(processingList);
            }, 1000);
        } else {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        }
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [processingList, pollStatus]);

    useEffect(() => {
        setFileData([]);
    }, [tabState, setFileData]);

    const viewReport = useCallback((id) => {
        if (!id) return;
        let url = `${window.location.origin}/investor/app/data-room/${id}?fetchType=all&page=1&type=scan&tab=dataRoom`;
        window.open(url, '_blank');
    }, []);

    const handleShowOptions = useCallback(
        (index) => {
            if (showOptions !== index) {
                setShowOptions(index);
            } else {
                setShowOptions(null);
            }
        },
        [showOptions],
    );

    const getOrgName = useCallback(
        (orgName, file, rowIndex) => {
            const isPreTagged = file?.status === 'pre-tagged' || (file?.status === 'processing' && orgName);
            const shouldAnimate = animationQueue.includes(file.id);

            return (
                <OrgNameCell
                    file={file}
                    orgName={orgName}
                    rowIndex={rowIndex}
                    isPreTagged={isPreTagged}
                    shouldAnimate={shouldAnimate}
                    selectOrg={selectOrg}
                    orgList={orgList}
                    index={index}
                    setIndex={setIndex}
                    setSearchText={setSearchText}
                    isSelectingOrg={isSelectingOrg[file.id] || false}
                    isMasterId={isMasterId}
                    viewReport={viewReport}
                    onOpenAddLead={handleOpenAddLead}
                />
            );
        },
        [animationQueue, orgList, selectOrg, setSearchText, setIndex, viewReport, isMasterId, isSelectingOrg, handleOpenAddLead],
    );

    const getDocType = useCallback(
        (docType, file, rowIndex, isLoading) => {
            const isPreTagged = (file?.status === 'pre-tagged' && !docType) || (file?.status === 'processing' && docType);
            const shouldAnimate = animationQueue.includes(file.id);

            return (
                <DocTypeCell
                    file={file}
                    docType={docType}
                    rowIndex={rowIndex}
                    isPreTagged={isPreTagged}
                    shouldAnimate={shouldAnimate}
                    selectDocType={selectDocType}
                    documentList={documentList}
                    isLoading={isLoading}
                    isSelectingDocType={isSelectingDocType[file.id] || false}
                    isFinalized={tabState === 'tagged'}
                />
            );
        },
        [animationQueue, documentList, selectDocType, isSelectingDocType, tabState],
    );

    const getLoadingText = useCallback((file) => {
        if (file.zip) {
            return 'Unzipping Document';
        }
        if (file.id.includes('temp')) {
            return 'Analyzing Document';
        }
        if (file.status === 'tagging') {
            return 'Tagging Organisation Name';
        }
        if (file.docType && !file.orgName) {
            return 'Identifying Organisation!';
        }

        if (!file.docType && !file.orgName) {
            return 'Identifying Doc Type';
        }

        if (file.docType && file.orgName) {
            return 'Identifying Organisation';
        }

        return 'Analyzing Document';
    }, []);

    const FilterOptions = (items: { label: string; value: string }[], key: string) => {
        scrollContainerRef.current?.scrollTo({ top: 0 });
        setPage(0);
        setFilters((prev) => {
            const updatedFilters = { ...prev, [key]: items };
            if (JSON.stringify(prev) !== JSON.stringify(updatedFilters)) {
                return updatedFilters; // Update state only if filters actually change
            }
            return prev;
        });
    };

    return (
        <>
            <FilteredPills filteredPills={filters} setFilteredPills={setFilters} />
            <div
                ref={scrollContainerRef}
                style={
                    tabState === 'all'
                        ? { minHeight: '70%', overflowY: 'auto', padding: '0 0 1rem 0', marginBottom: '7rem' }
                        : { minHeight: '90%', overflowY: 'auto', padding: '0 0 1rem 0', marginBottom: '10rem' }
                }
                className={styles.MagicUploadTableWrapper}
            >
                <table className={styles.MagicUploadTable}>
                    <thead style={{ position: 'sticky', top: '0', zIndex: '10' }}>
                        <tr className={styles.MUHeader}>
                            <th>File Name</th>
                            {!isCompany && (
                                <FilteredHeader
                                    style={{ width: '18rem' }}
                                    onFilter={FilterOptions}
                                    title={'Organisation Name'}
                                    items={filters?.assesseeOrgIdList ?? []}
                                    disabled={false}
                                />
                            )}
                            <th style={{ width: '18rem' }}>Data Type</th>
                            <FilteredHeader onFilter={FilterOptions} title={'Source'} items={filters?.sources ?? []} disabled={false} />
                            <FilteredHeader
                                onFilter={FilterOptions}
                                title={'Uploaded By'}
                                items={filters?.userIds ?? []}
                                disabled={false}
                            />
                            <th>Last Updated</th>
                            <th style={{ width: '2rem' }}></th>
                        </tr>
                    </thead>
                    <tbody className={styles.MUBody}>
                        {isEmpty(fileData) ? (
                            <EmptyDataState tabState={tabState} isCompany={isCompany} />
                        ) : (
                            <>
                                {fileData.map((file, index) => (
                                    <MagicUploadRows
                                        key={index}
                                        file={file}
                                        index={index}
                                        isCompany={isCompany}
                                        onOpenFile={openFile}
                                        submitPassword={submitPassword}
                                        setUpdatedFileData={setUpdatedFileData}
                                        loaderWidth={loaderWidth}
                                        getLoadingText={getLoadingText}
                                        getOrgName={getOrgName}
                                        getDocType={getDocType}
                                        renderThreeDotsOptions={renderThreeDotsOptions}
                                        dropdownRef={dropdownRef}
                                        handleShowOptions={handleShowOptions}
                                        showOptions={showOptions}
                                        onOpenAddLead={handleOpenAddLead}
                                    />
                                ))}
                            </>
                        )}

                        {isLoadingMore && (
                            <tr>
                                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem 0 1rem 0' }}>
                                    <span style={{ width: '100%' }}>
                                        <AIText
                                            text={'Loading more data'}
                                            style={{
                                                marginLeft: '0.5rem',
                                                fontFamily: 'Gilroy-Semibold',
                                                fontSize: '0.875rem',
                                            }}
                                            loadingDotsStyle={{
                                                color: 'var(--primary-text-colour)',
                                                fontSize: '0.875rem',
                                            }}
                                            showLoadingDots
                                        />
                                    </span>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {showAddLeadModal &&
                (rerouteAddLead ? (
                    <BulkAddModal initialText={buildBulkAddPrefillText(addLeadPrefill)} onClose={closeAddLeadModal} />
                ) : (
                    <AddToWatchlistV2
                        prefill={addLeadPrefill}
                        modalOpen={showAddLeadModal}
                        source="magic_upload"
                        closeModal={closeAddLeadModal}
                        investorOrgId={investorOrgId}
                        handleAlreadyAddedInPlace
                        onSuccess={handleAddLeadSuccess}
                    />
                ))}
            {openDrawer === 'docx' && activeFile && (
                <DocxDrawer
                    open
                    onClose={closeFile}
                    fileName={activeFile.fileName}
                    fileUrl={openFileUrl}
                    onDownload={() => downloadTheFile(activeFile.docId, activeFile.fileName)}
                    onAddToReports={isAddable(activeFile) ? (payload) => handleAddToReports(activeFile, payload) : undefined}
                    addingToReports={addingFileToReports}
                    addedToReports={addedFileToReports}
                    onUnsavedEditsPrompt={unsavedEditsPromptTracker(activeFile, 'DOCUMENT')}
                />
            )}
            {openDrawer === 'sheet' && activeFile && (
                <SheetEditorDrawer
                    open
                    onClose={closeFile}
                    fileName={activeFile.fileName}
                    fileUrl={openFileUrl}
                    mimeType={openFileMimeType}
                    onAddToReports={isAddable(activeFile) ? (payload) => handleAddToReports(activeFile, payload) : undefined}
                    addingToReports={addingFileToReports}
                    addedToReports={addedFileToReports}
                    onUnsavedEditsPrompt={unsavedEditsPromptTracker(activeFile, 'SPREADSHEET')}
                />
            )}
            {openDrawer === 'file' && activeFile && (
                <FileViewerDrawer
                    heading={activeFile.fileName}
                    fileName={activeFile.fileName}
                    fileUrl={openFileUrl}
                    mimeType={openFileMimeType}
                    assesseeOrgId={activeFile.orgId || undefined}
                    workspaceSource="MAGIC_UPLOAD"
                    onDownload={() => downloadTheFile(activeFile.docId, activeFile.fileName)}
                    onClose={closeFile}
                />
            )}
        </>
    );
};
export default React.memo(MagicUploadFilesTable);
